import { useState, useEffect } from "react";
import { ChevronDown, Search, HelpCircle, CreditCard, Users, Calendar, Camera, Shield, MessageCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import GlobalHeader from "@/components/GlobalHeader";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { BACKEND_URL, API } from "@/lib/api";


const FAQ_CATEGORIES = [
  {
    id: "general",
    title: "Allgemein",
    icon: HelpCircle,
    color: "bg-blue-100 text-blue-600",
    questions: [
      {
        q: "Was ist mokiplaner.com?",
        a: "mokiplaner.com ist eine Online-Plattform zur Planung und Verwaltung von Events aller Art. Ob Hochzeit, Geburtstag, Firmenfeier oder Babyparty - wir bieten alle Tools, die du für ein unvergessliches Event brauchst."
      },
      {
        q: "Ist die Nutzung kostenlos?",
        a: "Ja! Du kannst mit unserem FREE-Paket kostenlos starten. Es enthält grundlegende Funktionen für ein Event mit bis zu 50 Gästen. Für erweiterte Funktionen und mehr Gäste bieten wir Premium-Pakete an."
      },
      {
        q: "Wie erstelle ich ein Event?",
        a: "Nach der Registrierung klickst du auf 'Event erstellen', wählst den Event-Typ aus und gibst die grundlegenden Informationen ein. Du erhältst sofort einen teilbaren Link für deine Gäste."
      },
      {
        q: "Kann ich mehrere Events gleichzeitig verwalten?",
        a: "Mit dem FREE- und PREMIUM-Paket kannst du 1 Event verwalten. Mit unserem PRO Abo oder dem BUSINESS-Paket kannst du problemlos mehrere Events gleichzeitig planen und verwalten."
      },
      {
        q: "Wie lange ist mein Event verfügbar?",
        a: "Im FREE-Paket ist dein Event 3 Tage aktiv. Mit unserem PREMIUM- oder BUSINESS-Paket bleibt dein Event 365 Tage verfügbar. Im PRO Abo ist die Laufzeit unbegrenzt, solange das Abo aktiv ist."
      }
    ]
  },
  {
    id: "payment",
    title: "Zahlung & Pakete",
    icon: CreditCard,
    color: "bg-green-100 text-green-600",
    questions: [
      {
        q: "Welche Zahlungsmethoden akzeptiert ihr?",
        a: "Wir akzeptieren alle gängigen Kreditkarten (Visa, Mastercard, American Express), SEPA-Lastschrift und PayPal über unseren sicheren Zahlungsanbieter Stripe."
      },
      {
        q: "Wie kann ich einen Gutschein einlösen?",
        a: "Während des Bezahlvorgangs findest du ein Eingabefeld für Gutscheincodes. Gib deinen Code dort ein und der Rabatt wird automatisch vom Gesamtpreis abgezogen."
      },
      {
        q: "Kann ich mein Paket upgraden?",
        a: "Ja, du kannst jederzeit auf ein höheres Paket upgraden. Der verbleibende Betrag deines aktuellen Pakets wird anteilig angerechnet."
      },
      {
        q: "Wie kündige ich mein Abonnement?",
        a: "Du kannst dein Abonnement jederzeit in deinen Profileinstellungen kündigen. Die Kündigung wird zum Ende der aktuellen Abrechnungsperiode wirksam."
      },
      {
        q: "Gibt es eine Geld-zurück-Garantie?",
        a: "Ja! Wenn du mit unserem Service nicht zufrieden bist, kannst du innerhalb von 14 Tagen nach dem Kauf eine vollständige Erstattung beantragen."
      }
    ]
  },
  {
    id: "guests",
    title: "Gästeverwaltung",
    icon: Users,
    color: "bg-purple-100 text-purple-600",
    questions: [
      {
        q: "Wie lade ich Gäste ein?",
        a: "Du erhältst einen teilbaren Event-Link, den du per E-Mail, WhatsApp oder anderen Kanälen versenden kannst. Gäste können sich direkt über den Link anmelden."
      },
      {
        q: "Können Gäste ohne Registrierung teilnehmen?",
        a: "Ja, Gäste können sich mit Namen und E-Mail-Adresse anmelden, ohne ein eigenes Konto erstellen zu müssen."
      },
      {
        q: "Wie funktioniert die RSVP-Funktion?",
        a: "Gäste können über den Event-Link ihre Teilnahme bestätigen, absagen oder als 'vielleicht' markieren. Du siehst alle Antworten in deinem Event-Dashboard."
      }
    ]
  },
  {
    id: "events",
    title: "Event-Funktionen",
    icon: Calendar,
    color: "bg-orange-100 text-orange-600",
    questions: [
      {
        q: "Welche Event-Typen werden unterstützt?",
        a: "Wir unterstützen alle Event-Typen: Hochzeiten, Geburtstage, Firmenfeiern, Babypartys, Jubiläen, Konferenzen und viele mehr. Jeder Event-Typ hat speziell angepasste Funktionen."
      },
      {
        q: "Wie funktioniert die Musik-Playlist?",
        a: "Gäste können Musikwünsche einreichen. Du siehst alle Vorschläge und kannst sie für die Playlist freigeben oder ablehnen."
      },
      {
        q: "Gibt es einen Event-Chat?",
        a: "Ja! Alle Teilnehmer können über den integrierten Chat kommunizieren und Bilder teilen."
      }
    ]
  },
  {
    id: "media",
    title: "Fotos & Medien",
    icon: Camera,
    color: "bg-pink-100 text-pink-600",
    questions: [
      {
        q: "Wie viele Fotos kann ich hochladen?",
        a: "Die Anzahl der Fotos hängt von deinem gewählten Paket ab. Die genauen Limits findest du in der Paketübersicht auf unserer Preisseite."
      },
      {
        q: "In welchen Formaten kann ich Medien hochladen?",
        a: "Wir unterstützen alle gängigen Bildformate (JPG, PNG, GIF, WEBP) sowie Videos (MP4, MOV)."
      },
      {
        q: "Können Gäste auch Fotos hochladen?",
        a: "Ja! Gäste können Fotos direkt in die Event-Galerie hochladen. Du kannst diese Funktion in den Event-Einstellungen aktivieren oder deaktivieren."
      },
      {
        q: "Wie lange werden meine Fotos gespeichert?",
        a: "Deine Fotos bleiben so lange gespeichert, wie dein Event aktiv ist."
      }
    ]
  },
  {
    id: "privacy",
    title: "Datenschutz & Sicherheit",
    icon: Shield,
    color: "bg-red-100 text-red-600",
    questions: [
      {
        q: "Wie sicher sind meine Daten?",
        a: "Wir verwenden modernste Verschlüsselungstechnologien. Alle Daten werden auf sicheren Servern in Europa gespeichert."
      },
      {
        q: "Wer kann mein Event sehen?",
        a: "Nur Personen mit dem Event-Link können dein Event sehen."
      },
      {
        q: "Werden meine Daten an Dritte weitergegeben?",
        a: "Nein, wir geben keine persönlichen Daten an Dritte weiter. Details findest du in unserer Datenschutzerklärung."
      },
      {
        q: "Wie kann ich mein Konto löschen?",
        a: "Du kannst dein Konto jederzeit in den Profileinstellungen unter 'Konto löschen' entfernen. Alle deine Daten werden innerhalb von 30 Tagen unwiderruflich gelöscht."
      }
    ]
  },
  {
    id: "support",
    title: "Support",
    icon: MessageCircle,
    color: "bg-indigo-100 text-indigo-600",
    questions: [
      {
        q: "Wie erreiche ich den Support?",
        a: "Du erreichst uns per E-Mail an info@mokiplaner.com. Wir antworten in der Regel innerhalb von 24-48 Stunden."
      },
      {
        q: "Wann ist der Support erreichbar?",
        a: "Unser Support ist von Montag bis Freitag von 9:00 bis 17:00 Uhr erreichbar."
      },
      {
        q: "Kann ich Feature-Wünsche einreichen?",
        a: "Ja! Wir freuen uns über Feedback und Feature-Wünsche. Schreib uns einfach an info@mokiplaner.com."
      }
    ]
  }
];

export default function FAQ() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [openQuestions, setOpenQuestions] = useState({});
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const checkSession = async () => {
      try {
        const response = await axios.get(`${API}/auth/session`, { withCredentials: true });
        if (response.data.user) {
          setUser(response.data.user);
        }
      } catch {
        // Not logged in
      }
    };
    checkSession();
  }, []);

  const handleLogin = () => {
    window.location.href = `${API}/auth/login`;
  };

  const toggleQuestion = (categoryId, questionIndex) => {
    const key = `${categoryId}-${questionIndex}`;
    setOpenQuestions(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const filteredCategories = searchQuery
    ? FAQ_CATEGORIES.map(cat => ({
      ...cat,
      questions: cat.questions.filter(
        q => q.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
          q.a.toLowerCase().includes(searchQuery.toLowerCase())
      )
    })).filter(cat => cat.questions.length > 0)
    : FAQ_CATEGORIES;

  const displayCategories = selectedCategory
    ? filteredCategories.filter(c => c.id === selectedCategory)
    : filteredCategories;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex flex-col">
      {/* Global Header */}
      <GlobalHeader user={user} onLogin={handleLogin} showDashboard={true} />

      {/* Content - mit Padding für fixed Header */}
      <main className="flex-1 pt-20 pb-12">
        <div className="max-w-4xl mx-auto px-4 py-8">
          {/* Page Title */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Häufig gestellte Fragen</h1>
            <p className="text-gray-500 mt-2">Finde schnell Antworten auf deine Fragen</p>
          </div>

          {/* Search */}
          <div className="relative max-w-xl mx-auto mb-8">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              placeholder="Suche in allen Fragen..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 py-6 text-lg rounded-full border-gray-200 focus:border-indigo-300 focus:ring-indigo-200"
            />
          </div>

          {/* Category Pills */}
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            <Button
              variant={selectedCategory === null ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedCategory(null)}
              className="rounded-full"
            >
              Alle
            </Button>
            {FAQ_CATEGORIES.map(cat => (
              <Button
                key={cat.id}
                variant={selectedCategory === cat.id ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedCategory(cat.id)}
                className="rounded-full"
              >
                <cat.icon className="w-4 h-4 mr-1" />
                {cat.title}
              </Button>
            ))}
          </div>

          {/* FAQ Content */}
          {displayCategories.map(category => (
            <div key={category.id} className="mb-8">
              {/* Category Header */}
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${category.color}`}>
                  <category.icon className="w-5 h-5" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">{category.title}</h3>
                <span className="text-sm text-gray-500">({category.questions.length})</span>
              </div>

              {/* Questions */}
              <div className="space-y-3">
                {category.questions.map((item, idx) => {
                  const key = `${category.id}-${idx}`;
                  const isOpen = openQuestions[key];

                  return (
                    <motion.div
                      key={idx}
                      className="bg-white rounded-xl shadow-sm overflow-hidden"
                      initial={false}
                    >
                      <button
                        onClick={() => toggleQuestion(category.id, idx)}
                        className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                      >
                        <span className="font-medium text-gray-900 pr-4">{item.q}</span>
                        <ChevronDown className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                      </button>
                      <AnimatePresence>
                        {isOpen && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                          >
                            <div className="px-6 pb-4 text-gray-600 leading-relaxed border-t border-gray-100 pt-4">
                              {item.a}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          ))}

          {/* No Results */}
          {displayCategories.length === 0 && (
            <div className="text-center py-12">
              <HelpCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Keine Ergebnisse für &quot;{searchQuery}&quot;</p>
              <button
                onClick={() => setSearchQuery("")}
                className="text-indigo-600 hover:underline mt-2"
              >
                Suche zurücksetzen
              </button>
            </div>
          )}

          {/* Contact CTA */}
          <div className="mt-12 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-8 text-center">
            <h3 className="text-xl font-bold text-gray-900 mb-2">Keine Antwort gefunden?</h3>
            <p className="text-gray-600 mb-4">Unser Support-Team hilft dir gerne weiter!</p>
            <Button onClick={() => navigate('/kontakt')} className="rounded-full">
              Kontakt aufnehmen
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
